import { useState, useEffect, useCallback } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Users, Plus, Trash2, Loader2, Shield, ChefHat, ConciergeBell, KeyRound, Eye, EyeOff, Check, X, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

const ROLE_CONFIG = {
  patron: { label: 'Patron', icon: Shield, color: 'text-amber-500', bg: 'bg-amber-500/10', desc: 'Acces complet' },
  chef: { label: 'Chef', icon: ChefHat, color: 'text-blue-400', bg: 'bg-blue-400/10', desc: 'Carte, Menu du jour, Stocks' },
  service: { label: 'Service', icon: ConciergeBell, color: 'text-emerald-400', bg: 'bg-emerald-400/10', desc: 'Reservations, Scanner QR' },
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ email: '', password: '', nom: '', role: 'service' });
  const [saving, setSaving] = useState(false);
  const [showPasswords, setShowPasswords] = useState({});
  const [changingPassword, setChangingPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
    } catch { toast.error('Erreur chargement utilisateurs'); }
    setLoading(false);
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const createUser = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.password) return;
    setSaving(true);
    try {
      await api.post('/auth/users', formData);
      toast.success(`Utilisateur ${formData.email} cree`);
      setFormData({ email: '', password: '', nom: '', role: 'service' });
      setShowForm(false);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  const deleteUser = async (id, email) => {
    if (!window.confirm(`Supprimer ${email} ?`)) return;
    try {
      await api.delete(`/auth/users/${id}`);
      toast.success(`${email} supprime`);
      loadUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const changePassword = async (userId) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Mot de passe trop court (min 6 caracteres)');
      return;
    }
    try {
      await api.put(`/auth/users/${userId}/password`, { password: newPassword });
      toast.success('Mot de passe modifie');
      setChangingPassword(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[rgb(var(--lm-gold))]" /></div>;

  const groupedByRole = {};
  for (const role of ['patron', 'chef', 'service', 'scan']) {
    groupedByRole[role] = users.filter(u => u.role === role && u.status === 'approved');
  }
  const pendingUsers = users.filter(u => u.status === 'pending');

  const approveUser = async (id, name) => {
    try {
      await api.put(`/auth/users/${id}/approve`);
      toast.success(`${name} approuve !`);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const rejectUser = async (id, name) => {
    try {
      await api.put(`/auth/users/${id}/reject`);
      toast.success(`${name} rejete`);
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const changeRole = async (userId, newRole) => {
    try {
      await api.put(`/auth/users/${userId}/role`, { role: newRole });
      toast.success('Role modifie');
      loadUsers();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  return (
    <div className="space-y-6" data-testid="user-management">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Users size={18} className="text-[rgb(var(--lm-gold))]" />
          <h2 className="text-base font-title text-[rgb(var(--lm-text))]">Gestion des Acces</h2>
        </div>
        <Button size="sm" onClick={() => setShowForm(!showForm)}
          className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-8" data-testid="add-user-btn">
          <Plus size={14} className="mr-1" /> Nouvel utilisateur
        </Button>
      </div>

      {/* Pending approvals */}
      {pendingUsers.length > 0 && (
        <div className="bg-amber-50 rounded-xl border border-amber-200 p-4 space-y-3" data-testid="pending-users">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center animate-pulse"><UserCheck size={14} className="text-amber-600" /></div>
            <div>
              <h3 className="text-sm font-bold text-amber-800">Demandes en attente</h3>
              <p className="text-[10px] text-amber-600">{pendingUsers.length} compte{pendingUsers.length > 1 ? 's' : ''} a valider</p>
            </div>
          </div>
          <div className="space-y-2">
            {pendingUsers.map(u => (
              <div key={u.id} className="bg-white rounded-lg p-3 border border-amber-100 flex items-center justify-between" data-testid={`pending-${u.id}`}>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{u.name || u.nom || u.email}</p>
                  <p className="text-xs text-gray-400">{u.email}</p>
                  <p className="text-[10px] text-gray-300 mt-0.5">Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
                <div className="flex items-center gap-2">
                  <select defaultValue="chef" onChange={e => changeRole(u.id, e.target.value)}
                    className="text-xs border rounded-lg px-2 py-1 bg-white h-7" data-testid={`pending-role-${u.id}`}>
                    <option value="chef">Chef</option>
                    <option value="service">Service</option>
                  </select>
                  <Button onClick={() => approveUser(u.id, u.name || u.email)} size="sm" className="bg-green-500 hover:bg-green-600 text-white text-xs h-7" data-testid={`approve-${u.id}`}>
                    <Check size={12} className="mr-1" /> Accepter
                  </Button>
                  <Button onClick={() => rejectUser(u.id, u.name || u.email)} size="sm" variant="outline" className="border-red-200 text-red-500 hover:bg-red-50 text-xs h-7" data-testid={`reject-${u.id}`}>
                    <X size={12} />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create User Form */}
      {showForm && (
        <form onSubmit={createUser} className="lm-card p-4 space-y-3" data-testid="create-user-form">
          <h3 className="text-[10px] tracking-[0.15em] uppercase text-[rgb(var(--lm-text-muted))]">Nouveau compte</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase text-[rgb(var(--lm-text-muted))]">Nom</label>
              <Input value={formData.nom} onChange={e => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Prenom Nom" className="h-8 text-xs bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                data-testid="new-user-nom" />
            </div>
            <div>
              <label className="text-[9px] uppercase text-[rgb(var(--lm-text-muted))]">Role</label>
              <select value={formData.role} onChange={e => setFormData({ ...formData, role: e.target.value })}
                className="w-full h-8 text-xs rounded-md bg-[rgb(var(--lm-surface))] border border-[rgb(var(--lm-border))] text-[rgb(var(--lm-text))] px-2"
                data-testid="new-user-role">
                <option value="patron">Patron</option>
                <option value="chef">Chef</option>
                <option value="service">Service</option>
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase text-[rgb(var(--lm-text-muted))]">Email</label>
              <Input type="email" value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemple.com" className="h-8 text-xs bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                required data-testid="new-user-email" />
            </div>
            <div>
              <label className="text-[9px] uppercase text-[rgb(var(--lm-text-muted))]">Mot de passe</label>
              <Input type="password" value={formData.password} onChange={e => setFormData({ ...formData, password: e.target.value })}
                placeholder="Min 6 caracteres" className="h-8 text-xs bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                required minLength={6} data-testid="new-user-password" />
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <Button type="submit" size="sm" disabled={saving}
              className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-bg))] hover:opacity-90 text-xs h-7" data-testid="create-user-submit">
              {saving ? <Loader2 size={12} className="animate-spin mr-1" /> : <Plus size={12} className="mr-1" />}
              Creer
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={() => setShowForm(false)}
              className="text-[rgb(var(--lm-text-muted))] text-xs h-7">
              Annuler
            </Button>
          </div>
        </form>
      )}

      {/* Users by Role */}
      {['patron', 'chef', 'service'].map(role => {
        const config = ROLE_CONFIG[role];
        const roleUsers = groupedByRole[role];
        const Icon = config.icon;

        return (
          <div key={role} className="lm-card p-4" data-testid={`role-section-${role}`}>
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-7 h-7 rounded-lg ${config.bg} flex items-center justify-center`}>
                <Icon size={14} className={config.color} />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[rgb(var(--lm-text))]">{config.label}</h3>
                <p className="text-[9px] text-[rgb(var(--lm-text-muted))]">{config.desc}</p>
              </div>
              <span className="ml-auto text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{roleUsers.length}</span>
            </div>

            {roleUsers.length === 0 ? (
              <p className="text-xs text-[rgb(var(--lm-text-muted))] italic pl-9">Aucun utilisateur</p>
            ) : (
              <div className="space-y-2 pl-9">
                {roleUsers.map(user => (
                  <div key={user.id} className="flex items-center gap-3 py-1.5 group" data-testid={`user-${user.id}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[rgb(var(--lm-text))]">{user.nom || user.email}</p>
                      <p className="text-[10px] text-[rgb(var(--lm-text-muted))]">{user.email}</p>
                    </div>

                    {changingPassword === user.id ? (
                      <div className="flex items-center gap-1.5">
                        <Input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)}
                          placeholder="Nouveau mdp" className="h-6 w-32 text-[10px] bg-[rgb(var(--lm-surface))] border-[rgb(var(--lm-border))]"
                          data-testid={`change-pass-input-${user.id}`} />
                        <button onClick={() => changePassword(user.id)}
                          className="text-[10px] text-[rgb(var(--lm-gold))] hover:underline">OK</button>
                        <button onClick={() => { setChangingPassword(null); setNewPassword(''); }}
                          className="text-[10px] text-[rgb(var(--lm-text-muted))] hover:underline">X</button>
                      </div>
                    ) : (
                      <button onClick={() => setChangingPassword(user.id)}
                        className="opacity-0 group-hover:opacity-100 text-[rgb(var(--lm-text-muted))] hover:text-[rgb(var(--lm-gold))] transition-all"
                        title="Changer mot de passe" data-testid={`change-pass-${user.id}`}>
                        <KeyRound size={12} />
                      </button>
                    )}

                    <button onClick={() => deleteUser(user.id, user.email)}
                      className="opacity-0 group-hover:opacity-100 text-[rgb(var(--lm-red)/0.4)] hover:text-[rgb(var(--lm-red))] transition-all"
                      data-testid={`delete-user-${user.id}`}>
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default UserManagement;
